import { trackingReducer } from "./state/tracking-slice.js";
import { recordWriteFailure, recordingReducer } from "./state/recording-slice.js";
import { createTrackingQualityListenerMiddleware, trackingQualityReducer } from "./state/tracking-quality.js";
import { createPersistenceMiddleware, slicePrefixOf } from "./state/persistence-middleware.js";
import { arElementsReducer, gpsDataReducer, gpsElementsReducer, sanitizeForDevTools, setColdStartOverrideEnabled, setCompassExperimentEnabled, setCompassRotationPriorEnabled, setCompassVoteWeight, setCompassWebXRConsistencyEnabled, setConsensusSolverComparisonEnabled, setZeroPos, validateLicenseKey } from "gps-plus-slam-js";
import { configureStore, createAction, createListenerMiddleware } from "@reduxjs/toolkit";
import { COMMUNITY_LICENSE_KEY } from "gps-plus-slam-js/community-license-key";
//#region ../src/state/diagnostics-action.ts
/**
* `diagnostics/note` — an action dispatched to be RECORDED, and for nothing
* else (owner decision, 2026-08-23).
*
* **WHY THIS EXISTS.** Some of the most useful numbers a session produces are
* measurements the app makes about itself: how long the AR entry waited before
* it was ready, how long the elevation estimator took to engage. They are shown
* on screen as a toast, which is enough to read one of them on a walk and
* nothing at all afterwards. Putting them in the persisted action stream means
* a recording can be opened later and asked what actually happened.
*
* **WHY IT HAS NO REDUCER, AND WHY THAT IS THE POINT.** The owner's requirement
* was that it be *"not really consumed by the store"*. The persistence
* middleware writes each action AFTER `next(action)` has run, and every slice
* is a plain RTK `createSlice` that ignores an unmatched type — so an action
* with no reducer at all is persisted exactly like any other and changes
* nothing. A reducer added later would make the action ambiguous: is the
* recording the record, or is the state? `diagnostics-action.test.ts` asserts
* the absence.
*
* ⚠️ **AND WHY IT IS INERT IN SOME APPS TODAY.** The middleware only writes
* while a session is recording, into whatever `StorageBackend` the store was
* built with. The OSM demo builds its store with a `NullStorageBackend` by
* explicit design — *"this demo records nothing, and a real backend here would
* start writing GPS actions to OPFS behind the user's back"* — so diagnostics
* dispatched there are dropped until that decision changes. Shipped anyway, at
* the owner's instruction, so the day the demo records nothing else has to be
* built. The trade is written up in
* `GpsPlusSlamJs_Docs/docs/2026-08-23-2335-diagnostic-actions-into-recordings-findings.md`.
*
* **The prefix is the whole contract.** `createPersistenceMiddleware` keeps a
* whitelist of slice prefixes and drops everything else **silently** — no
* warning, no error. `diagnostics` is in the store factory's built-in list,
* derived through `slicePrefixOf(recordDiagnostic.type)` rather than written as
* a literal, for the reason recorded there: a literal `refPointsV2/` outlived
* its rename once and took a whole slice's actions out of every recording.
*
* @see diagnostics-action.ts.md
*/
/**
* Record a diagnostic note into the session's action stream.
*
* A no-op unless a recording is running and the store was built with a real
* `StorageBackend`; nothing observes it in state, by design.
*/
const recordDiagnostic = createAction("diagnostics/note");
//#endregion
//#region ../src/state/slam-app-store-listener.ts
/**
* Build a listener middleware that applies the requested compass opt-ins once
* `gpsData` exists and a flag is still unset. Register it via `.prepend(...)`
* (so its effect dispatches *outside* the trigger's `next()` — see module doc).
*
* Behaviour:
*  - **Predicate** fires whenever the `gpsData` *object reference* is new since
*    the last apply AND at least one opt-in is still unset. Two terms, each load-
*    bearing:
*    - `s.gpsData !== lastApplied` makes the predicate edge-triggered on gpsData
*      identity. A recreated `gpsData` (store swap / origin reset) is a fresh
*      object, so it still re-triggers the apply — the re-apply semantics the
*      2026-06-27 field bug demands are preserved. Do NOT weaken this to a
*      `null → non-null` transition: that would drop the re-apply. The point of
*      keying on the *reference* (not on "a flag is unset") is that it also stops
*      the effect from re-firing for the SAME gpsData.
*    - `optIns.some((o) => !o.isSet(s))` keeps the no-op case (all flags already
*      set) from scheduling an effect at all.
*    Why the reference guard matters: without it the predicate is purely level-
*    based, so if an opt-in's `apply` dispatches but `isSet` never flips true
*    (e.g. consumer/library **version skew** where the action type no longer
*    matches the reducer — the packages are published independently), the
*    condition stays true forever and every effect dispatch re-arms it: an
*    unbounded storm that freezes the app. The per-dispatch `isSet` re-check
*    below guards only against dispatching an *already-set* flag twice; it does
*    nothing when the flag never sets. The reference guard is what bounds it.
*  - **Effect** records the gpsData reference it is acting on, then dispatches
*    every still-unset opt-in as a top-level action. `isSet` is re-read against
*    the *current* store state immediately before each dispatch (not one snapshot
*    at effect entry): a dispatch is synchronous, so a flag is already set by the
*    time the next check runs, and an opt-in's own dispatch can re-enter this
*    effect before the loop finishes. Re-checking per dispatch makes that
*    re-entrancy idempotent — a flag is dispatched only while still unset.
*/
function createSlamAppStoreListenerMiddleware(optIns) {
	const listenerMiddleware = createListenerMiddleware();
	let lastApplied = null;
	listenerMiddleware.startListening({
		predicate: (_action, currentState) => {
			const s = currentState;
			return s.gpsData !== null && s.gpsData !== lastApplied && optIns.some((optIn) => !optIn.isSet(s));
		},
		effect: (_action, api) => {
			const entry = api.getState();
			if (entry.gpsData === null) return;
			lastApplied = entry.gpsData;
			for (const optIn of optIns) {
				const s = api.getState();
				if (s.gpsData === null) return;
				if (!optIn.isSet(s)) optIn.apply((action) => {
					api.dispatch(action);
				});
			}
		}
	});
	return listenerMiddleware.middleware;
}
//#endregion
//#region ../src/state/create-slam-app-store.ts
/**
* `createSlamAppStore` — composable Redux store factory for AR+GPS apps.
*
* Introduced in Iter 1 of the AppFramework/RecorderApp boundary migration.
* Wires the three library reducers (`gpsData`, `gpsElements`, `arElements`),
* the framework's recording lifecycle slice, and the persistence middleware.
*
* Recorder-only state (routing screen, ref-points, scenario) is plugged in
* by the consumer via `extraReducers` / `extraMiddleware`. The factory itself
* never references those concepts so apps that don't need them (POI viewers,
* navigation arrows, …) compose freely.
*
* This is the framework's ONLY store factory (the legacy
* `createRecorderStore`/`store.ts` it replaced moved out in Iter 1D); the
* core library's license error messages point here as the remediation.
*
* @see gps-plus-slam/GpsPlusSlamJs_Docs/docs/2026-05-03-appframework-vs-recorderapp-boundary-analysis.md — Iter 1
*/
/**
* Slice prefixes the framework always persists, derived from the actual
* library / framework action creators (never hand-typed). A rename of the
* `gpsData` or `recording` slice therefore propagates here automatically
* instead of silently dropping that slice's actions from recordings.
*/
const BUILTIN_PERSISTED_PREFIXES = [
	slicePrefixOf(setZeroPos.type),
	slicePrefixOf(recordWriteFailure.type),
	slicePrefixOf(recordDiagnostic.type)
];
/**
* Consumer summariser THEN framework sanitizer.
*
* Exported so the ORDER is testable. Inline in the factory it was reachable
* only through the devtools extension, i.e. not at all from a test -- and the
* order is the whole safety property: the consumer collapses its own large
* slice, and the framework then redacts pose and GPS data from what is left.
* Reversed, a consumer summariser that dropped fields could drop the redaction
* with them.
*/
function composeStateSanitizer(consumer, framework) {
	if (consumer === void 0) return framework;
	return (state) => framework(consumer(state));
}
/**
* Build a Redux store wired with library + recording slices, persistence
* middleware, and any caller-supplied extras. See module docstring for the
* design rationale.
*/
function createSlamAppStore(options) {
	const { storageBackend, extraReducers, extraMiddleware, persistedExtraPrefixes, onWriteFailure, persistWhile, continuousActionIndex, enableDevChecks = true, licenseKey = COMMUNITY_LICENSE_KEY, trackingQualityOptions, enableCompassColdStartOverride = true, enableCompassRotationPrior = false, enableCompassWebXRConsistency = false, enableCompassExperiment = false, enableConsensusSolverComparison = false, compassVoteWeight, serializableIgnoredActions, serializableIgnoredPaths, immutableIgnoredPaths, devToolsStateSanitizer } = options;
	validateLicenseKey(licenseKey);
	const builtins = {
		gpsData: gpsDataReducer,
		gpsElements: gpsElementsReducer,
		arElements: arElementsReducer,
		recording: recordingReducer,
		tracking: trackingReducer,
		trackingQuality: trackingQualityReducer
	};
	const reservedPrefixOnlyKeys = [slicePrefixOf(recordDiagnostic.type)];
	if (extraReducers) {
		const reserved = [...Object.keys(builtins), ...reservedPrefixOnlyKeys];
		const collisions = Object.keys(extraReducers).filter((key) => reserved.includes(key));
		if (collisions.length > 0) throw new Error(`extraReducers must not overwrite framework-reserved slice(s): ${collisions.join(", ")}. Reserved keys: ${reserved.join(", ")}.`);
	}
	const reducer = {
		...builtins,
		...extraReducers ?? {}
	};
	const trackingQualityMiddleware = createTrackingQualityListenerMiddleware(trackingQualityOptions);
	const booleanOptInRows = [
		{
			enabled: enableCompassColdStartOverride,
			flag: "coldStartOverrideEnabled",
			setFlag: setColdStartOverrideEnabled,
			recordWhenFalse: true
		},
		{
			enabled: enableCompassRotationPrior,
			flag: "compassRotationPriorEnabled",
			setFlag: setCompassRotationPriorEnabled
		},
		{
			enabled: enableCompassWebXRConsistency,
			flag: "compassWebXRConsistencyEnabled",
			setFlag: setCompassWebXRConsistencyEnabled
		},
		{
			enabled: enableCompassExperiment,
			flag: "compassExperimentEnabled",
			setFlag: setCompassExperimentEnabled
		},
		{
			enabled: enableConsensusSolverComparison,
			flag: "consensusSolverComparisonEnabled",
			setFlag: setConsensusSolverComparisonEnabled
		}
	];
	const isDecided = (flag) => (s) => s.gpsData?.[flag] !== void 0;
	const compassOptIns = booleanOptInRows.filter((row) => row.enabled || row.recordWhenFalse).map(({ enabled, flag, setFlag }) => ({
		isSet: isDecided(flag),
		apply: (dispatch) => dispatch(setFlag(enabled))
	}));
	if (compassVoteWeight !== void 0) compassOptIns.push({
		isSet: (s) => s.gpsData?.compassVoteWeight !== void 0,
		apply: (dispatch) => dispatch(setCompassVoteWeight(compassVoteWeight))
	});
	const prependedListeners = [trackingQualityMiddleware];
	if (compassOptIns.length > 0) prependedListeners.push(createSlamAppStoreListenerMiddleware(compassOptIns));
	const persistenceMiddleware = createPersistenceMiddleware({
		storageBackend,
		onWriteFailure,
		persistWhile,
		continuousActionIndex,
		persistedPrefixes: [...BUILTIN_PERSISTED_PREFIXES, ...persistedExtraPrefixes ?? []]
	});
	const store = configureStore({
		reducer,
		middleware: (getDefaultMiddleware) => getDefaultMiddleware({
			serializableCheck: enableDevChecks ? {
				ignoredActions: ["tracking/poseReceived", ...serializableIgnoredActions ?? []],
				...serializableIgnoredPaths === void 0 ? {} : { ignoredPaths: [...serializableIgnoredPaths] }
			} : false,
			immutableCheck: enableDevChecks ? { ignoredPaths: ["tracking", ...immutableIgnoredPaths ?? []] } : false
		}).prepend(...prependedListeners).concat(persistenceMiddleware, ...extraMiddleware ?? []),
		devTools: {
			actionSanitizer: sanitizeForDevTools,
			stateSanitizer: composeStateSanitizer(devToolsStateSanitizer, sanitizeForDevTools)
		}
	});
	return {
		getState: () => store.getState(),
		dispatch: store.dispatch,
		subscribe: (listener) => store.subscribe(listener),
		writeFrame: (blob, index) => storageBackend.writeFrame(blob, index),
		writeSessionMetadata: (metadata) => storageBackend.writeSessionMetadata(metadata),
		flushPendingActionWrites: () => persistenceMiddleware.flushPendingWrites()
	};
}
//#endregion
export { createSlamAppStore as n, recordDiagnostic as r, composeStateSanitizer as t };
